import { useState, useMemo, useEffect, useRef, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { toast } from '@/components/ui/sonner';
import { DataTable } from '@/components/ui/data-table';
import { FilterMenu } from '@/components/shared/filter-menu';
import { PaginationControls } from '@/components/ui/pagination';
import {
  useDeleteSource,
  useResyncSource,
  useUpdateSource,
  useSourcesPaginated,
  useSourceStatusUpdates,
  useUpdateSourceCategorization
} from '@/hooks/use-sources';
import { usePermissions } from '@/hooks/use-permissions';
import { sourcePromoteUrl } from '@/constants/paths';
import { storageService } from '@/services';
import { getSourceColumns } from './source-columns';
import { AddToCategoryDialog } from './dialogs/add-to-category-dialog';
import { RenameDialog } from './dialogs/rename-dialog';
import type { Source } from '@/types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { SOURCE_FILTERS } from '../constants';
import type { SourceFilterType } from '../types';

interface SourcesTableProps {
  categrizationId?: string;
  isUncategorised?: boolean;
  searchQuery?: string;
  /** Filters shown before the type filter, on the same row. */
  leading?: ReactNode;
}

export function SourcesTable({ categrizationId, isUncategorised = false, searchQuery = '', leading }: SourcesTableProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeFilter, setActiveFilter] = useState<SourceFilterType>('all');

  // Kept in the URL (same reason as the SOPs tab's page — see sources.tsx) so opening a
  // source's detail page and hitting Back restores the exact page you were on, not page 1.
  // Uses its own param name (srcPage) so it doesn't collide with the SOPs tab's `page`.
  const page = Number(searchParams.get('srcPage')) || 1;
  const setPage = (nextPage: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (nextPage > 1) next.set('srcPage', String(nextPage));
      else next.delete('srcPage');
      return next;
    });
  };

  // Search is debounced before hitting the backend — it's now a server-side filter (see
  // below), so refetching on every keystroke would otherwise thrash the list.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  // Only reset to page 1 when the filter/search actually changes — not on the initial
  // mount, or a restored Back-navigation URL with srcPage > 1 would get wiped out
  // immediately after loading.
  const isFirstFilterRender = useRef(true);
  useEffect(() => {
    if (isFirstFilterRender.current) {
      isFirstFilterRender.current = false;
      return;
    }
    setPage(1);
  }, [categrizationId, isUncategorised, activeFilter, debouncedSearch]);

  // page/type/uncategorised/search are all sent to the backend — this list previously
  // had no server-side limit at all, so it happened to render everything, but with no
  // pagination UI a large account would just get slower and slower to render. Now it
  // pages properly like the SOPs table does.
  const queryParams = useMemo(() => {
    const params: {
      page: number;
      limit: number;
      categrizationId?: string;
      uncategorised?: boolean;
      type?: string;
      search?: string;
    } = { page, limit: 20 };
    if (categrizationId) params.categrizationId = categrizationId;
    else if (isUncategorised) params.uncategorised = true;
    if (activeFilter !== 'all') params.type = activeFilter;
    if (debouncedSearch) params.search = debouncedSearch;
    return params;
  }, [page, categrizationId, isUncategorised, activeFilter, debouncedSearch]);

  const { data: sourcesData, isLoading, refetch } = useSourcesPaginated(queryParams);
  const sources = sourcesData?.sources;
  const pagination = sourcesData?.pagination;

  const deleteSourceMutation = useDeleteSource();
  const resyncSourceMutation = useResyncSource();
  const updateSourceMutation = useUpdateSource();
  const updateCategorizationMutation = useUpdateSourceCategorization();
  const [sourceToDelete, setSourceToDelete] = useState<Source | null>(null);
  const [sourceToCategorize, setSourceToCategorize] = useState<Source | null>(null);
  const [sourceToRename, setSourceToRename] = useState<Source | null>(null);
  const [optimisticInProgress, setOptimisticInProgress] = useState<Set<string>>(new Set());
  const { can } = usePermissions();
  const canEdit = can('edit', 'sources');

  // Listen for real-time source status updates
  useSourceStatusUpdates();

  // Poll every 3s while any source is INPROGRESS so the table updates without needing a manual refresh
  const hasInProgress = (sources ?? []).some((s) => s.status === 'INPROGRESS') || optimisticInProgress.size > 0;
  useEffect(() => {
    if (!hasInProgress) return;
    const interval = setInterval(() => { void refetch(); }, 3000);
    return () => clearInterval(interval);
  }, [hasInProgress, refetch]);

  // Clear optimistic state once server confirms source is no longer INPROGRESS
  useEffect(() => {
    if (optimisticInProgress.size === 0) return;
    const done = (sources ?? []).filter(
      (s) => optimisticInProgress.has(s.id) && s.status !== 'INPROGRESS',
    );
    if (done.length > 0) {
      setOptimisticInProgress((prev) => {
        const next = new Set(prev);
        done.forEach((s) => next.delete(s.id));
        return next;
      });
    }
  }, [sources, optimisticInProgress]);

  // All real filtering (category, uncategorised, type, search) now happens server-side —
  // this just overlays the optimistic "just submitted" status onto the current page.
  const filteredSources = useMemo(() => {
    if (!sources) return [];
    if (optimisticInProgress.size === 0) return sources;
    return sources.map((s) => (optimisticInProgress.has(s.id) ? { ...s, status: 'INPROGRESS' as const } : s));
  }, [sources, optimisticInProgress]);

  const handleDelete = (source: Source) => {
    setSourceToDelete(source);
  };

  const confirmDelete = async () => {
    if (!sourceToDelete) return;

    try {
      // If it's a file source, delete from storage first
      if (sourceToDelete.type === 'FILE' && sourceToDelete.metadata?.key) {
        try {
          // Generate presigned delete URL and delete
          await storageService.generateDeleteUrl(sourceToDelete.metadata.key as string);
          // Also try direct delete
          await storageService.deleteFile(sourceToDelete.metadata.key as string);
        } catch (storageError) {
          console.warn('Storage delete error (continuing with source delete):', storageError);
        }
      }

      await deleteSourceMutation.mutateAsync(sourceToDelete.id);
      toast.success('Source deleted successfully');
      setSourceToDelete(null);
    } catch (error) {
      toast.error('Failed to delete source');
      console.error('Delete source error:', error);
    }
  };

  const handleResync = async (source: Source) => {
    setOptimisticInProgress((prev) => new Set(prev).add(source.id));
    try {
      const result = await resyncSourceMutation.mutateAsync(source.id);
      if (result.status === 'unchanged') {
        toast.info('Document is already up to date');
        setOptimisticInProgress((prev) => { const next = new Set(prev); next.delete(source.id); return next; });
      } else {
        toast.success('Resync started, this may take a moment');
      }
    } catch {
      toast.error('Failed to resync source');
      setOptimisticInProgress((prev) => { const next = new Set(prev); next.delete(source.id); return next; });
    }
  };

  const handleAddToCategory = async (category: string, subcategory: string) => {
    if (!sourceToCategorize) return;
    try {
      await updateCategorizationMutation.mutateAsync({ id: sourceToCategorize.id, category, subcategory });
      toast.success('Source added to category');
      setSourceToCategorize(null);
    } catch {
      toast.error('Failed to add source to category');
    }
  };

  const handleRename = async (name: string) => {
    if (!sourceToRename) return;
    try {
      await updateSourceMutation.mutateAsync({ id: sourceToRename.id, data: { name } });
      toast.success('Source renamed');
      setSourceToRename(null);
    } catch {
      toast.error('Failed to rename source');
    }
  };

  const columns = getSourceColumns({
    onDelete: handleDelete,
    canDelete: canEdit,
    onResync: handleResync,
    resyncingSourceIds: optimisticInProgress,
    onRename: setSourceToRename,
    canRename: canEdit,
    onAddToCategory: setSourceToCategorize,
    canAddToCategory: canEdit,
    onPromoteKnowledge: can('view', 'promoted-blueprint')
      ? (source) => navigate(sourcePromoteUrl(source.id))
      : undefined,
  });

  return (
    <>
      <div className='mb-4 flex flex-wrap items-center gap-2'>
        {leading}
        <FilterMenu
          label='Type'
          top={0}
          options={SOURCE_FILTERS.filter((f) => f.value !== 'all')}
          value={activeFilter === 'all' ? undefined : activeFilter}
          onChange={(value) => setActiveFilter((value as SourceFilterType | undefined) ?? 'all')}
        />
      </div>

      <DataTable
        columns={columns}
        data={filteredSources}
        isLoading={isLoading}
        onRowClick={(source) => {
          if (source.type === 'LINK') return;
          navigate(`/training/sources/${source.id}`);
        }}
        enableSorting={true}
        enableFiltering={true}
        enablePagination={false}
        enableColumnVisibility={false}
        enableRowSelection={false}
        emptyMessage={
          activeFilter !== 'all'
            ? 'No sources found matching your search or filter.'
            : 'No sources found. Create your first source to get started.'
        }
      />

      {pagination && <PaginationControls page={pagination.page} totalPages={pagination.totalPages} onPageChange={setPage} />}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!sourceToDelete} onOpenChange={(open) => !open && setSourceToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription className='break-all'>
              {(sourceToDelete?.queryUsageCount ?? 0) > 0
                ? `This source helped answer ${sourceToDelete!.queryUsageCount} ${sourceToDelete!.queryUsageCount === 1 ? 'query' : 'queries'}. `
                : ''}
              Deleting &ldquo;{sourceToDelete?.name}&rdquo; is permanent and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteSourceMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              disabled={deleteSourceMutation.isPending}
              className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
            >
              {deleteSourceMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AddToCategoryDialog
        open={!!sourceToCategorize}
        onOpenChange={(open) => !open && setSourceToCategorize(null)}
        isSubmitting={updateCategorizationMutation.isPending}
        onSubmit={handleAddToCategory}
      />

      <RenameDialog
        open={!!sourceToRename}
        onOpenChange={(open) => !open && setSourceToRename(null)}
        currentName={sourceToRename?.name ?? ''}
        isSubmitting={updateSourceMutation.isPending}
        onSubmit={handleRename}
      />
    </>
  );
}
